// @file Surface share links store
import { trackEvent } from '@@/bits/analytics'
import { getAuthorDisplayName } from '@@/bits/author'
import { CONFIRM_TURN_OFF } from '@@/bits/confirmation_dialog'
import { captureFetchException } from '@@/bits/error_tracker'
import { removeHashidPrefix } from '@@/bits/hashid_helper'
import { __ } from '@@/bits/intl'
import {
  authWithReferrerUrl,
  buildUrlFromPath,
  currentUrl,
  getSearchParam,
  navigateTo,
  transformUrl,
} from '@@/bits/location'
import { postColorIdToPostColor, POST_COLOR_STRINGS } from '@@/bits/post_color'
import { getCustomPropertyValue } from '@@/bits/post_properties'
import PromiseQueue from '@@/bits/promise_queue'
import { getOrderedColorGroupsWithTitle } from '@@/bits/surface_posts_grouping_helper'
import {
  DEFAULT_SUBMISSION_REQUEST_CONFIRMATION_TEXT,
  getAuthorBreakoutPath,
  getBreakoutDisabledUrl,
  getColorBreakoutPath,
  getSectionBreakoutPath,
  getSingleSelectBreakoutPath,
  getSubmissionRequestConfirmationUrl,
  getSubmissionRequestDisabledUrl,
  isAuthorBreakoutLink,
  isColorBreakoutLink,
  isSectionBreakoutLink,
  isSingleSelectBreakoutLink,
} from '@@/bits/surface_share_links_helper'
import { isRegistered } from '@@/bits/user_model'
import { unboundedWatch, unboundedWatchEffect } from '@@/bits/vue'
import { SnackbarNotificationType, WallWishGroupByTypes } from '@@/enums'
import { useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { useGlobalInputDialogStore } from '@@/pinia/global_input_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type {
  BreakoutLinkConfig,
  PostAttributes,
  PostColor,
  PostColorId,
  SingleSelectOption,
  WallCustomPostSingleSelectProperty,
  WallGroupPost,
  WallGroupSingleSelect,
  WallSectionBreakoutLink,
  WallSubmissionRequestLink,
  WallSubmissionRequestProps,
} from '@@/types'
import { useCopyToClipboard } from '@@/vuecomposables/copy_to_clipboard'
import { keyBy } from 'es-toolkit'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

// replication of options in WallShareLinks::SubmissionRequest::RedirectLocations
export enum SubmissionRequestRedirectLocation {
  Padlet = 'padlet',
  Confirmation = 'confirmation',
  RequestAgain = 'request_again',
}

export const useSurfaceShareLinksStore = defineStore('surfaceShareLinks', () => {
  const surfaceStore = useSurfaceStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceMapStore = useSurfaceMapStore()
  const globalInputDialogStore = useGlobalInputDialogStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceSettingsStore = useSurfaceSettingsStore()
  const surfaceStartingStateStore = useSurfaceStartingStateStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()

  const queue = new PromiseQueue()

  /* Section Breakout */
  // State
  const sectionBreakoutLinks = ref<Record<number, WallSectionBreakoutLink> | null>(null)

  // Getters
  const sectionBreakoutIdentifier = computed<string | undefined>(() => {
    if (!isSectionBreakout.value) return undefined
    return breakoutLinkConfig.value?.id
  })

  // Actions
  async function enableGroupBySections(): Promise<void> {
    try {
      surfaceSettingsStore.startWallAttributesPreview()
      surfaceSettingsStore.toggleGroupBySection(true)
      surfaceSettingsStore.stopWallAttributesPreview()
      void fetchSectionBreakoutLinks()
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e)
    }
  }
  function setSectionBreakoutLinksState(links: WallSectionBreakoutLink[] | null): void {
    if (links !== null) {
      sectionBreakoutLinks.value = keyBy(links, (link) => link.wall_section_id)
    } else {
      sectionBreakoutLinks.value = null
    }
  }

  async function fetchSectionBreakoutLinks(): Promise<void> {
    if (isSectionBreakout.value || surfaceStore.isSubmissionRequest || !surfacePermissionsStore.canIAdminister) {
      // early exit if not supposed to show section breakout links
      return
    }
    try {
      const { SectionBreakoutApi } = await import('@@/surface/api/section_breakout')
      const links = await SectionBreakoutApi.list(surfaceStore.wallId)
      if (links == null) {
        setSectionBreakoutLinksState(null)
        return
      }
      sectionBreakoutLinks.value = keyBy(links, (link) => link.wall_section_id)
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelListSectionBreakoutLinks' })
    }
  }

  async function copySectionBreakoutLinkAndAlert(sectionId: number): Promise<void> {
    if (sectionBreakoutLinks.value === null) return

    const sectionBreakoutLink = sectionBreakoutLinks.value[sectionId]
    if (sectionBreakoutLink === undefined) {
      // This can happen when trying to copy breakout links for non-sectioned groups
      // (e.g., when grouping by author/color). Show an error message.
      globalSnackbarStore.setSnackbar({
        message: __('Breakout room links are only available for sections'),
        notificationType: SnackbarNotificationType.error,
      })
      return
    }

    await useCopyToClipboard().copyToClipboard({
      text: sectionBreakoutLink.link,
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
      },
    })

    trackEvent('Breakout rooms panel', 'Copied breakout room link', null, null, { link: sectionBreakoutLink.link })
  }

  async function copyGroupBreakoutLinkAndAlert(groupId: number | string): Promise<void> {
    let breakoutUrl: string | undefined = undefined

    switch (surfaceWishArrangementStore.wishGroupBy) {
      case WallWishGroupByTypes.Section: {
        breakoutUrl = sectionBreakoutLinks.value?.[groupId as number]?.link
        break
      }
      case WallWishGroupByTypes.Color: {
        const { getColorBreakoutUrl } = await import('@@/bits/surface_share_links_helper')
        breakoutUrl = getColorBreakoutUrl(surfaceStore.namespace, groupId as PostColorId, surfaceStore.wallHashid)
        break
      }
      default: {
        if (
          surfaceWishArrangementStore.isGroupBySingleSelect &&
          surfaceWishArrangementStore.customPropIdFromWishGroupBy
        ) {
          const { getSingleSelectBreakoutUrl } = await import('@@/bits/surface_share_links_helper')
          breakoutUrl = getSingleSelectBreakoutUrl(
            surfaceStore.namespace,
            surfaceWishArrangementStore.customPropIdFromWishGroupBy,
            groupId as string,
            surfaceStore.wallHashid,
          )
        }
        break
      }
    }

    if (breakoutUrl === undefined) {
      globalSnackbarStore.setSnackbar({
        message: __('Breakout room link not available'),
        notificationType: SnackbarNotificationType.error,
      })
      return
    }

    await useCopyToClipboard().copyToClipboard({
      text: breakoutUrl,
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
      },
    })

    trackEvent('Breakout rooms panel', 'Copied breakout room link', null, null, { link: breakoutUrl })
  }

  function showSectionInputDialog(): void {
    const title = __('Section %{number}', {
      number: surfaceSectionsStore.numOfSections + 1,
    })

    const payload = {
      title: __('New section'),
      inputValue: title,
      shouldFadeIn: true,
      inputPlaceholder: 'Section title',
      inputAriaLabel: __('Section title'),
      submitButtonText: 'Create',
      submitActions: [
        async ({ inputValue }) => {
          await surfaceSectionsStore.createSectionWithName(inputValue.trim())
        },
      ],
      validationActions: [
        ({ inputValue }) => {
          globalInputDialogStore.setSubmitButtonDisable(inputValue.trim().length === 0)
        },
      ],
    }
    globalInputDialogStore.openInputDialog(payload)
  }

  async function handleSectionBreakoutUpdated(): Promise<void> {
    return await fetchSectionBreakoutLinks()
  }

  async function handleBreakoutDisabled(): Promise<void> {
    if (isBreakout.value) {
      if (isSectionBreakout.value) {
        setSectionBreakoutLinksState(null)
      }
      navigateTo(getBreakoutDisabledUrl())
      return
    }
    if (sectionBreakoutLinks.value) {
      setSectionBreakoutLinksState(null)
    }
  }

  // #region breakout links
  const breakoutLinkConfig = computed<BreakoutLinkConfig | undefined>(
    () => surfaceStartingStateStore.startingState.breakoutLink,
  )
  const breakoutLinkPath = computed<string>(() => {
    if (breakoutLinkConfig.value == null) return ''

    if (isColorBreakoutLink(breakoutLinkConfig.value)) {
      return getColorBreakoutPath(surfaceStore.namespace, breakoutLinkConfig.value.color, surfaceStore.wallHashid)
    } else if (isSectionBreakoutLink(breakoutLinkConfig.value)) {
      return getSectionBreakoutPath(surfaceStore.namespace, breakoutLinkConfig.value.id)
    } else if (isAuthorBreakoutLink(breakoutLinkConfig.value)) {
      return getAuthorBreakoutPath(surfaceStore.namespace, surfaceStore.wallHashid)
    } else if (isSingleSelectBreakoutLink(breakoutLinkConfig.value)) {
      return getSingleSelectBreakoutPath(
        surfaceStore.namespace,
        breakoutLinkConfig.value.custom_prop_id,
        breakoutLinkConfig.value.option_id,
        surfaceStore.wallHashid,
      )
    }
    return ''
  })
  const isBreakout = computed(() => breakoutLinkConfig.value != null)

  const getBreakoutLinkPostPath = (postIdOrHashid: string): string => {
    return breakoutLinkPath.value + `/wish/${postIdOrHashid}`
  }

  const getBreakoutLinkPostUrl = (postIdOrHashid: string): string => {
    return buildUrlFromPath(getBreakoutLinkPostPath(postIdOrHashid))
  }

  const copyBreakoutPostLinkAndAlert = async (postIdOrHashid: string): Promise<void> => {
    const url = getBreakoutLinkPostUrl(postIdOrHashid)
    await useCopyToClipboard().copyToClipboard({
      text: url,
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
      },
    })
  }

  function isValidPostOrDraftForBreakout(
    postOrDraft: Pick<PostAttributes, 'wall_section_id' | 'color' | 'author_id' | 'custom_properties'>,
  ): boolean {
    if (isSectionBreakout.value) {
      return postOrDraft.wall_section_id === sectionBreakoutSection.value?.id
    }

    if (isColorBreakout.value) {
      return postOrDraft.color === colorBreakoutPostColor.value
    }

    if (isAuthorBreakout.value) {
      // For drafts, author_id might not be set, but drafts always belong to current user
      return postOrDraft.author_id === surfaceCurrentUserStore.currentUser?.id || postOrDraft.author_id == null
    }

    if (isSingleSelectBreakout.value && singleSelectBreakoutProperty.value) {
      const optionId = getCustomPropertyValue(postOrDraft, singleSelectBreakoutProperty.value)
      return optionId === singleSelectBreakoutOptionId.value
    }

    return true
  }

  // Section breakout
  const isSectionBreakout = computed<boolean>(() => breakoutLinkConfig.value?.type === 'section_breakout')
  const sectionBreakoutSection = computed(() => {
    if (breakoutLinkConfig.value?.type !== 'section_breakout') return null
    return surfaceSectionsStore.getSectionById(breakoutLinkConfig.value.wall_section_id)
  })

  // Color breakout links
  const isColorBreakout = computed<boolean>(() => breakoutLinkConfig.value?.type === 'color_breakout')
  const colorBreakoutPostColor = computed<PostColor>(() => {
    if (!isBreakout.value || !breakoutLinkConfig.value) return undefined
    if (breakoutLinkConfig.value.type !== 'color_breakout') return undefined

    const colorId: PostColorId = breakoutLinkConfig.value.color

    return postColorIdToPostColor(colorId)
  })

  // Author breakout links
  const isAuthorBreakout = computed<boolean>(() => breakoutLinkConfig.value?.type === 'author_breakout')

  // Single select breakout links
  const isSingleSelectBreakout = computed<boolean>(() => breakoutLinkConfig.value?.type === 'single_select_breakout')

  // The ID of the single-select custom property on the breakout link
  const singleSelectBreakoutCustomPropId = computed<string | undefined>(() => {
    if (!isBreakout.value || !breakoutLinkConfig.value) return undefined
    if (breakoutLinkConfig.value.type !== 'single_select_breakout') return undefined

    return breakoutLinkConfig.value.custom_prop_id
  })

  // The ID of the specific option within the single-select property on the breakout link
  const singleSelectBreakoutOptionId = computed<string | undefined>(() => {
    if (!isBreakout.value || !breakoutLinkConfig.value) return undefined
    if (breakoutLinkConfig.value.type !== 'single_select_breakout') return undefined

    return breakoutLinkConfig.value.option_id
  })

  // The full custom property object resolved from the custom property ID on the breakout link
  const singleSelectBreakoutProperty = computed<WallCustomPostSingleSelectProperty | undefined>(() => {
    if (singleSelectBreakoutCustomPropId.value == null) return undefined

    return surfacePostPropertiesStore.wallSingleSelectPropsByCustomPropertyId[singleSelectBreakoutCustomPropId.value]
  })

  // The specific option object on the single-select custom property, resolved from the option ID on the breakout link
  const singleSelectionBreakoutOption = computed(() => {
    if (singleSelectBreakoutProperty.value == null) return undefined
    return singleSelectBreakoutProperty.value.selection_options.find(
      (option) => option.id === singleSelectBreakoutOptionId.value,
    )
  })

  const breakoutLinkGroups = computed<WallGroupPost[]>(() => {
    if (breakoutLinkConfig.value == null) return []

    switch (breakoutLinkConfig.value.type) {
      case 'section_breakout': {
        const sectionId = breakoutLinkConfig.value.wall_section_id
        const section = surfaceSectionsStore.getSectionById(sectionId)
        return section ? [section] : []
      }
      case 'color_breakout': {
        const color = breakoutLinkConfig.value.color
        const colorGroup = getOrderedColorGroupsWithTitle({
          isLightColorScheme: surfaceStore.colorScheme === 'light',
        }).find((group) => group.id === color)
        return colorGroup ? [colorGroup] : []
      }
      case 'author_breakout': {
        if (surfaceCurrentUserStore.currentUser == null) return []
        return [
          {
            id: removeHashidPrefix(surfaceCurrentUserStore.currentUser.hashid),
            title: getAuthorDisplayName(surfaceCurrentUserStore.currentUser),
            avatar: surfaceCurrentUserStore.currentUser.avatar,
          },
        ]
      }
      case 'single_select_breakout': {
        const group: WallGroupSingleSelect = {
          id: breakoutLinkConfig.value.option_id,
          title: singleSelectionBreakoutOption.value?.name ?? '',
        }
        return [group]
      }
      default:
        return []
    }
  })

  // Whether any breakout links on this wall have ever been visited.
  const breakoutLinksVisited = computed<boolean>(
    () => surfaceStartingStateStore.startingState.wall.breakout_links_visited,
  )
  // #endregion

  /* Submission Request */
  // State
  const isEnablingSubmissionRequest = ref(false)
  const isSubmissionRequestEnabled = ref(false)
  const submissionRequestLink = ref<string | null>(null)
  const submissionRequestRequiresLogin = ref<boolean | null>(null)
  const submissionRequestRedirectLocation = ref<string | null>(null)
  const submissionRequestConfirmationText = ref<string>()
  const disableSubmissionRequestTimer = ref<ReturnType<typeof setTimeout> | undefined>()
  const disableSubmissionRequestSnackbarUid = ref<string | null>(null)

  // Getters
  const submissionRequestSectionId = computed<number | null>(() => {
    if (!surfaceStore.isSubmissionRequest) return null

    const sectionIdParam = getSearchParam('section')
    if (sectionIdParam == null) return null

    // Validate that section exists
    const sectionId = parseInt(sectionIdParam)
    const section = surfaceSectionsStore.getSectionById(sectionId)
    return section == null ? null : sectionId
  })

  const submissionRequestColorId = computed<PostColor | null>(() => {
    if (!surfaceStore.isSubmissionRequest) return null

    const colorIdParam = getSearchParam('color')
    if (colorIdParam == null) return null

    // Validate that color exists
    if (!POST_COLOR_STRINGS.includes(colorIdParam as PostColorId)) return null

    // We store default color as null in the backend, so no need to set color for default color
    return colorIdParam === 'default' ? null : (colorIdParam as PostColor)
  })

  const submissionRequestSingleSelectId = computed<{
    singleSelectId: WallCustomPostSingleSelectProperty['id']
    optionId: SingleSelectOption['id']
  } | null>(() => {
    if (!surfaceStore.isSubmissionRequest) return null

    const singleSelectIdParam = getSearchParam('single_select')
    if (singleSelectIdParam == null) return null

    const [singleSelectId, optionId] = singleSelectIdParam.split(':')
    if (singleSelectId == null || optionId == null) return null

    // Find the single select to check if the option id is valid
    const singleSelectOptionId = surfacePostPropertiesStore.findSingleSelectPropertyOptionId({
      propertyId: singleSelectId,
      optionId,
    })
    if (singleSelectOptionId == null) return null

    return { singleSelectId, optionId }
  })

  // Actions
  function setSubmissionRequestAttributes(linkAttributes: WallSubmissionRequestLink | null): void {
    if (linkAttributes != null) {
      submissionRequestLink.value = linkAttributes.link
      submissionRequestRequiresLogin.value = linkAttributes.requires_login
      submissionRequestRedirectLocation.value = linkAttributes.redirect_location
      submissionRequestConfirmationText.value =
        linkAttributes?.confirmation_text ?? DEFAULT_SUBMISSION_REQUEST_CONFIRMATION_TEXT
      isSubmissionRequestEnabled.value = true
    } else {
      submissionRequestLink.value = null
      submissionRequestRequiresLogin.value = null
      submissionRequestRedirectLocation.value = null
      submissionRequestConfirmationText.value = undefined
      isSubmissionRequestEnabled.value = false
    }
  }

  async function fetchSubmissionRequestLink(): Promise<void> {
    // if user is not admin & not in submission request view, we perform early exit to avoid showing section breakout links
    if ((isSectionBreakout.value || !surfacePermissionsStore.canIAdminister) && !surfaceStore.isSubmissionRequest) {
      return
    }

    try {
      const { SubmissionRequestApi } = await import('@@/surface/api/submission_request')
      const linkAttributes = await SubmissionRequestApi.fetch(surfaceStore.wallId)

      if (linkAttributes == null) {
        setSubmissionRequestAttributes(null)

        if (surfaceStore.isSubmissionRequest) {
          // Redirect user if is in submissionRequestPage that no longer exists
          void navigateToSubmissionRequestDisabledPage()
        }

        return
      }
      if (surfaceStore.isSubmissionRequest && linkAttributes.requires_login && !isRegistered(surfaceStore.user)) {
        navigateTo(authWithReferrerUrl(currentUrl()))
        return
      }

      setSubmissionRequestAttributes(linkAttributes)
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelFetchSubmissionRequestLink' })
    }
  }

  async function enableSubmissionRequest(): Promise<void> {
    // Clear any existing callbacks to disable
    if (disableSubmissionRequestTimer.value !== undefined) clearTimeout(disableSubmissionRequestTimer.value)
    globalSnackbarStore.removeSnackbar(disableSubmissionRequestSnackbarUid.value ?? '')
    const { SubmissionRequestApi } = await import('@@/surface/api/submission_request')
    const linkAttributes = await SubmissionRequestApi.enable(
      surfaceStore.wallId,
      DEFAULT_SUBMISSION_REQUEST_CONFIRMATION_TEXT,
    )
    setSubmissionRequestAttributes(linkAttributes)
  }

  async function disableSubmissionRequest(): Promise<void> {
    try {
      const { SubmissionRequestApi } = await import('@@/surface/api/submission_request')
      await SubmissionRequestApi.disable(surfaceStore.wallId)

      setSubmissionRequestAttributes(null)
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelDisableSubmissionRequest' })
    }
  }

  async function disableSubmissionRequestWithUndo(): Promise<void> {
    const SNACKBAR_DURATION = 3000

    disableSubmissionRequestSnackbarUid.value = globalSnackbarStore.setSnackbar({
      notificationType: SnackbarNotificationType.success,
      message: __('Submission form closed'),
      actionText: 'Undo',
      persist: true,
      actionTextActions: [
        () => {
          // revert and close snackbar
          isSubmissionRequestEnabled.value = true
          if (disableSubmissionRequestTimer.value !== undefined) clearTimeout(disableSubmissionRequestTimer.value)
          globalSnackbarStore.removeSnackbar(disableSubmissionRequestSnackbarUid.value ?? '')
        },
      ],
    })

    // To hide Submission form first to give illusion of deletion
    isSubmissionRequestEnabled.value = false

    // Timeout to close snackbar and disable section breakout links
    disableSubmissionRequestTimer.value = setTimeout(() => {
      globalSnackbarStore.removeSnackbar(disableSubmissionRequestSnackbarUid.value ?? '')
      void disableSubmissionRequest()
    }, SNACKBAR_DURATION)
  }

  function askToDisableSubmissionRequest(): void {
    void useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...CONFIRM_TURN_OFF,
      title: __('Turn off submission form?'),
      body: __('Current form will no longer work. Turning form back on will create a new form.'),
      afterConfirmActions: [disableSubmissionRequestWithUndo],
    })
  }

  async function toggleSubmissionRequest(): Promise<void> {
    const originalValue = isSubmissionRequestEnabled.value
    try {
      // don't allow changes if mid-change
      if (isEnablingSubmissionRequest.value) return

      if (isSubmissionRequestEnabled.value) {
        await askToDisableSubmissionRequest()
      } else {
        // Only show loading state if toggling to True
        isEnablingSubmissionRequest.value = true
        await enableSubmissionRequest()
        isEnablingSubmissionRequest.value = false
      }
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelToggleSubmissionRequest' })
      isSubmissionRequestEnabled.value = originalValue
    }
  }

  function getSubmissionRequestProps(
    payload: { requiresLogin?: boolean; redirectLocation?: string; confirmationText?: string } = {},
  ): WallSubmissionRequestProps | null {
    const requiresLogin =
      payload.requiresLogin !== undefined ? payload.requiresLogin : submissionRequestRequiresLogin.value
    const redirectLocation =
      payload.redirectLocation !== undefined ? payload.redirectLocation : submissionRequestRedirectLocation.value
    const confirmationText =
      payload.confirmationText !== undefined ? payload.confirmationText : submissionRequestConfirmationText.value

    if (requiresLogin == null || redirectLocation == null) return null

    return {
      requires_login: requiresLogin,
      redirect_location: redirectLocation,
      confirmation_text: confirmationText ?? DEFAULT_SUBMISSION_REQUEST_CONFIRMATION_TEXT,
    }
  }

  async function updateSubmissionRequestProps(
    oldProps: WallSubmissionRequestProps,
    newProps: WallSubmissionRequestProps,
  ): Promise<void> {
    try {
      const { SubmissionRequestApi } = await import('@@/surface/api/submission_request')
      const response = await SubmissionRequestApi.update(surfaceStore.wallId, {
        requires_login: newProps.requires_login,
        redirect_location: newProps.redirect_location,
        confirmation_text: newProps.confirmation_text,
      })
      submissionRequestConfirmationText.value = response.confirmation_text
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceSharePanelUpdateSubmissionRequestProps' })
      submissionRequestRedirectLocation.value = oldProps.redirect_location
      submissionRequestRequiresLogin.value = oldProps.requires_login
      submissionRequestConfirmationText.value = oldProps.confirmation_text
    }
  }

  async function enqueueToggleSubmissionRequestRequiresLogin(): Promise<void> {
    if (submissionRequestRequiresLogin.value === null) return

    const oldProps = getSubmissionRequestProps()
    const newProps = getSubmissionRequestProps({ requiresLogin: !submissionRequestRequiresLogin.value })

    if (oldProps === null || newProps === null) return

    // Optimistically update new value on the frontend immediately, before queueing the update request to the backend, for faster response
    submissionRequestRequiresLogin.value = !submissionRequestRequiresLogin.value

    void queue.enqueue(
      'updateSubmissionRequestProps',
      async () => await updateSubmissionRequestProps(oldProps, newProps),
    )
  }

  async function enqueueUpdateSubmissionRequestRedirectLocation(redirectLocation: string): Promise<void> {
    if (submissionRequestRedirectLocation.value === null) return
    if (submissionRequestRedirectLocation.value === redirectLocation) return

    const oldProps = getSubmissionRequestProps()
    const newProps = getSubmissionRequestProps({ redirectLocation })

    if (oldProps === null || newProps === null) return

    // Optimistically update new value on the frontend immediately, before queueing the update request to the backend, for faster response
    submissionRequestRedirectLocation.value = redirectLocation

    void queue.enqueue(
      'updateSubmissionRequestProps',
      async () => await updateSubmissionRequestProps(oldProps, newProps),
    )
  }

  async function enqueueUpdateSubmissionConfirmationText(confirmationText: string): Promise<void> {
    if (submissionRequestConfirmationText.value === confirmationText) return

    const oldProps = getSubmissionRequestProps()
    const newProps = getSubmissionRequestProps({ confirmationText })

    if (oldProps === null || newProps === null) return

    // Optimistically update new value on the frontend immediately, before queueing the update request to the backend, for faster response
    submissionRequestConfirmationText.value = confirmationText

    void queue.enqueue(
      'updateSubmissionRequestProps',
      async () => await updateSubmissionRequestProps(oldProps, newProps),
    )
  }

  async function copySubmissionRequestLinkAndAlert(): Promise<void> {
    if (submissionRequestLink.value === null) return

    await useCopyToClipboard().copyToClipboard({
      text: submissionRequestLink.value,
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
      },
    })

    trackEvent('Submission form panel', 'Copied submission form link', null, null, {
      link: submissionRequestLink.value,
    })
  }

  async function showPostPublishedSnackbar(): Promise<void> {
    void globalSnackbarStore.setSnackbar({
      message: __('Post published'),
      notificationType: SnackbarNotificationType.success,
    })
  }

  async function startNewSubmissionRequest(): Promise<void> {
    const attributes: PostAttributes = {}
    attributes.wall_section_id = submissionRequestSectionId.value ?? surfaceSectionsStore.defaultSectionId
    attributes.color = submissionRequestColorId.value
    if (submissionRequestSingleSelectId.value?.singleSelectId) {
      attributes.custom_properties = {
        [submissionRequestSingleSelectId.value.singleSelectId]: submissionRequestSingleSelectId.value.optionId,
      }
    }

    // There should be only one active draft at a time for submission request
    // So we don't start a new one if there is already an active draft
    if (surfaceDraftsStore.activeDraft != null) {
      surfaceDraftsStore.updateActiveDraft(attributes)
      return
    }

    if (surfaceStore.isMap) {
      const mapAttributes =
        attributes.wall_section_id == null ? attributes : { ...attributes, sectionId: attributes.wall_section_id }
      surfaceMapStore.startPickingLocation(mapAttributes)
    } else {
      void surfacePostsStore.startNewPost({ attributes })
    }
  }

  async function executeSubmissionRequestPostSubmitAction(): Promise<void> {
    switch (submissionRequestRedirectLocation.value) {
      case SubmissionRequestRedirectLocation.Padlet:
        navigateTo(surfaceStore.links.show)
        break
      case SubmissionRequestRedirectLocation.Confirmation:
        void navigateToSubmissionRequestConfirmationPage()
        break
      case SubmissionRequestRedirectLocation.RequestAgain:
        void showPostPublishedSnackbar()
        void startNewSubmissionRequest()
        break
    }
  }

  async function copySubmissionRequestSectionLinkAndAlert(sectionId: string | number): Promise<void> {
    if (submissionRequestLink.value === null) return

    switch (surfaceWishArrangementStore.wishGroupBy) {
      case WallWishGroupByTypes.Section:
        await copySubmissionRequestGroupLinkAndAlert({ section: sectionId.toString() })
        break
      case WallWishGroupByTypes.Color:
        await copySubmissionRequestGroupLinkAndAlert({ color: sectionId as string })
        break
      default: {
        if (surfaceWishArrangementStore.customPropIdFromWishGroupBy == null) return
        await copySubmissionRequestGroupLinkAndAlert({
          single_select: `${surfaceWishArrangementStore.customPropIdFromWishGroupBy}:${sectionId}`,
        })
        break
      }
    }
  }

  async function copySubmissionRequestGroupLinkAndAlert(payload: Record<string, string>): Promise<void> {
    if (submissionRequestLink.value === null) return

    await useCopyToClipboard().copyToClipboard({
      text: transformUrl(submissionRequestLink.value, {
        search: payload,
      }),
      globalSnackbarOptions: {
        message: __('Link copied to clipboard'),
        notificationType: SnackbarNotificationType.success,
      },
    })

    trackEvent('Submission form panel', 'Copied submission form link', null, null, {
      link: submissionRequestLink.value,
    })
  }

  async function navigateToSubmissionRequestDisabledPage(): Promise<void> {
    navigateTo(getSubmissionRequestDisabledUrl())
  }

  async function navigateToSubmissionRequestConfirmationPage(): Promise<void> {
    if (submissionRequestLink.value !== null) {
      navigateTo(getSubmissionRequestConfirmationUrl(submissionRequestLink.value))
    }
  }

  async function handleSubmissionRequestUpdated(): Promise<void> {
    return await fetchSubmissionRequestLink()
  }

  // Should always have section breakout links enabled when sections are enabled
  void unboundedWatchEffect(() => {
    if (surfaceSectionsStore.wallHasSections && sectionBreakoutLinks.value == null) {
      void fetchSectionBreakoutLinks()
    }
  })

  // #region - Single select submission form prefill

  // Custom properties are fetched after the submission request is started,
  // so we need to start a new submission request to ensure the single select prefill
  // works in single select submission form once the fetch is complete.
  void unboundedWatch(
    () => surfacePostPropertiesStore.hasFetchedInitialCustomPostProperties,
    (newHasFetchedInitialCustomPostProperties) => {
      if (
        newHasFetchedInitialCustomPostProperties &&
        surfaceStore.isSubmissionRequest &&
        getSearchParam('single_select') != null
      ) {
        void startNewSubmissionRequest()
      }
    },
  )

  // #endregion

  return {
    /* Section Breakout */
    // State
    sectionBreakoutLinks,
    sectionBreakoutIdentifier,

    // Actions
    enableGroupBySections,
    fetchSectionBreakoutLinks,
    copySectionBreakoutLinkAndAlert,
    copyGroupBreakoutLinkAndAlert,
    showSectionInputDialog,
    setSectionBreakoutLinksState,
    handleSectionBreakoutUpdated,
    handleBreakoutDisabled,

    // Breakout links
    breakoutLinkConfig,
    breakoutLinkPath,
    isBreakout,
    isValidPostOrDraftForBreakout,
    isSectionBreakout,
    sectionBreakoutSection,
    isColorBreakout,
    colorBreakoutPostColor,
    isAuthorBreakout,
    isSingleSelectBreakout,
    singleSelectBreakoutCustomPropId,
    singleSelectBreakoutOptionId,
    singleSelectBreakoutProperty,
    breakoutLinkGroups,
    getBreakoutLinkPostPath,
    getBreakoutLinkPostUrl,
    copyBreakoutPostLinkAndAlert,
    breakoutLinksVisited,

    /* Submission Request */
    // State
    isEnablingSubmissionRequest,
    isSubmissionRequestEnabled,
    submissionRequestLink,
    submissionRequestRequiresLogin,
    submissionRequestRedirectLocation,
    submissionRequestConfirmationText,

    // Getters
    submissionRequestSectionId,
    submissionRequestColorId,
    submissionRequestSingleSelectId,

    // Actions
    fetchSubmissionRequestLink,
    setSubmissionRequestAttributes,
    toggleSubmissionRequest,
    enqueueToggleSubmissionRequestRequiresLogin,
    enqueueUpdateSubmissionRequestRedirectLocation,
    enqueueUpdateSubmissionConfirmationText,
    copySubmissionRequestLinkAndAlert,
    copySubmissionRequestSectionLinkAndAlert,
    copySubmissionRequestGroupLinkAndAlert,
    startNewSubmissionRequest,
    executeSubmissionRequestPostSubmitAction,
    showPostPublishedSnackbar,
    handleSubmissionRequestUpdated,
  }
})
